// CustomerRewardTransactionsReport.jsx — "Customer Reward Transactions" (withdrawal & reward history)
// drill-down from Total Customers.

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import PrintIcon from '@mui/icons-material/Print';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeLabel, paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CustomerReportShell, {
  DateRangeFilter,
  ReportGrid,
  SectionCard,
  displayVpa,
  formatINR,
  formatISTDateTime,
  parseApiDate,
  useCanViewCustomerSensitive,
  validateDateRange,
} from '../../components/CustomerReportShell';

const formatNote = (noteStr) => {
  if (!noteStr) return 'No description available';
  if (noteStr === 'auto_add_from_game') return 'Game Points';
  if (noteStr === 'manual_from_app') return 'Withdrawal';
  return noteStr;
};

const CustomerRewardTransactionsReport = () => {
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [rewardTransactions, setRewardTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  const canViewSensitive = useCanViewCustomerSensitive();

  const { customerId } = useParams();

  useEffect(() => {
    fetchRewardTransactionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, startDate, endDate, paymentMode]); // [CASH-MODE] refetch on mode change

  const fetchRewardTransactionData = async () => {
    const rangeError = validateDateRange(startDate, endDate);
    if (rangeError) {
      setDialogMessage(rangeError);
      setOpenDialog(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE_URL}/customerReport/rewardTransactions`,
        {
          customerId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          page: 1,
          limit: 1000,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setCustomerName(data.customerName || '');
      setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
      setRewardTransactions((data.transactions || []).map((item, index) => {
        // withdrawals carry no payment mode (API maps manual_from_app to "Withdrawal")
        const isWithdrawal = item.action === 'manual_from_app' || item.action === 'reward_withdraw'
          || item.description === 'manual_from_app' || item.description === 'Withdrawal';
        return {
          id: index + 1,
          serialNumber: index + 1,
          customerName: data.customerName || '',
          amount: parseFloat(item.amount || 0),
          rawDate: item.createdAt || item.dateTime,
          dateTime: formatISTDateTime(item.createdAt || item.dateTime),
          note: formatNote(item.description),
          vpa: item.vpa,
          transactionId: item.transactionId || '',
          paymentMode: item.paymentMode, // [CASH-MODE]
          isWithdrawal,
        };
      }));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching data.');
      setRewardTransactions([]);
      setTotalsByMode(null);
    }
    setLoading(false);
  };

  const rewards = rewardTransactions.filter((t) => !t.isWithdrawal);
  const withdrawals = rewardTransactions.filter((t) => t.isWithdrawal);
  const totalRewards = rewards.reduce((s, t) => s + t.amount, 0);
  const totalWithdrawn = withdrawals.reduce((s, t) => s + t.amount, 0);
  const grandTotal = rewardTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  // [CASH-MODE] reward credits split by UPI / Cash (withdrawals excluded)
  const rewardModeTotals = totalsByMode || totalsByModeFromRows(rewards, (t) => t.amount);

  const handlePrint = () => {
    if (rewardTransactions.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }
    const doc = new jsPDF('l', 'pt', 'a4');
    const center = doc.internal.pageSize.getWidth() / 2;
    doc.setFontSize(16);
    doc.text('Customer Reward Transactions Report', center, 40, { align: 'center' });
    doc.setFontSize(12);
    if (customerName) doc.text(`Customer: ${customerName}`, center, 60, { align: 'center' });
    doc.text(`From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}`, center, customerName ? 80 : 60, { align: 'center' });

    doc.autoTable({
      startY: customerName ? 100 : 80,
      head: [['S.No.', 'Customer', 'Amount', 'Date & Time', 'Note', 'Transaction ID', 'Mode']],
      body: rewardTransactions.map((t) => [
        t.serialNumber,
        t.customerName,
        `Rs.${t.amount.toFixed(2)}`,
        t.dateTime,
        t.note,
        String(t.transactionId),
        t.isWithdrawal ? '-' : paymentModeLabel(t), // [CASH-MODE]
      ]),
      foot: [['', '', `Rs.${grandTotal.toFixed(2)}`, 'Grand Total', `Rewards Rs.${totalRewards.toFixed(2)} / Withdrawn Rs.${totalWithdrawn.toFixed(2)}`, '', '']],
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 10, overflow: 'linebreak' },
      margin: { right: 30, left: 30 },
    });
    doc.save('Customer_Reward_Transactions_Report.pdf');
  };

  const columns = [
    { field: 'serialNumber', headerName: '#', width: 60 },
    { field: 'rawDate', headerName: 'Date & Time (IST)', width: 190, type: 'dateTime',
      valueGetter: (v) => parseApiDate(v), valueFormatter: (v) => formatISTDateTime(v) },
    {
      field: 'isWithdrawal', headerName: 'Type', width: 130,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value ? 'Withdrawal' : 'Reward'}
          color={p.value ? 'default' : 'success'}
          variant={p.value ? 'outlined' : 'filled'}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
    {
      field: 'amount', headerName: 'Amount', width: 140, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 800, width: '100%', textAlign: 'right', color: p.row.isWithdrawal ? 'error.main' : 'success.dark' }}>
          {p.row.isWithdrawal ? '- ' : '+ '}{formatINR(p.value)}
        </Typography>
      ),
    },
    // [CASH-MODE] UPI / Cash badge (withdrawal rows have no mode)
    {
      field: 'paymentMode', headerName: 'Mode', width: 100,
      renderCell: (params) => (params.row.isWithdrawal ? <Typography variant="body2" color="text.secondary">-</Typography> : <PaymentModeChip row={params.row} />),
    },
    { field: 'vpa', headerName: 'VPA', width: 190, valueGetter: (v) => displayVpa(v, canViewSensitive) },
    {
      field: 'transactionId', headerName: 'Transaction ID', width: 220,
      renderCell: (p) => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{String(p.value || '-')}</Typography>,
    },
  ];

  return (
    <CustomerReportShell
      title="Withdrawals & Rewards"
      subtitle={`Reward credits and withdrawals · ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}`}
      icon={<PaymentsIcon />}
      customerId={customerId}
      customerName={customerName}
      actions={(
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={rewardTransactions.length === 0} sx={{ fontWeight: 700 }}>
          Print Report
        </Button>
      )}
      filters={(
        <DateRangeFilter startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate}>
          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
        </DateRangeFilter>
      )}
      error={error}
      onRetry={fetchRewardTransactionData}
      statsLoading={loading}
      stats={[
        { label: 'Rewards Credited', value: formatINR(totalRewards), hint: `${rewards.length} credit${rewards.length === 1 ? '' : 's'}`, color: 'success', icon: <AddCircleOutlineIcon /> },
        { label: 'Withdrawn', value: formatINR(totalWithdrawn), hint: `${withdrawals.length} withdrawal${withdrawals.length === 1 ? '' : 's'}`, color: 'error', icon: <RemoveCircleOutlineIcon /> },
        { label: 'Rewards · UPI', value: formatINR(rewardModeTotals.UPI && rewardModeTotals.UPI.amount), hint: `${(rewardModeTotals.UPI && rewardModeTotals.UPI.count) || 0} record(s)`, color: 'secondary', icon: <QrCode2Icon /> },
        { label: 'Rewards · Cash', value: formatINR(rewardModeTotals.CASH && rewardModeTotals.CASH.amount), hint: `${(rewardModeTotals.CASH && rewardModeTotals.CASH.count) || 0} record(s)`, color: 'warning', icon: <LocalAtmIcon /> },
      ]}
    >
      <SectionCard title="Transaction History" subtitle={`${rewardTransactions.length} record${rewardTransactions.length === 1 ? '' : 's'}`}>
        <ReportGrid
          rows={rewardTransactions}
          columns={columns}
          loading={loading}
          emptyTitle="No reward transactions found"
          emptyMessage="Try a different date range."
        />
      </SectionCard>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Alert</DialogTitle>
        <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </CustomerReportShell>
  );
};

export default CustomerRewardTransactionsReport;
